// @flow
import type { Advertiser } from 'app/packages/pinterest-flow-types/advertiser';
import type { AdvertiserShape } from 'app/packages/pinterest-flow-types/sterling';
import type {
  CampaignType,
  InterestType,
  KeywordOptionType,
  QuickPromoteTargetingType,
} from 'app/common/react/components/partner/QuickPromote/types';
import {
  validateBudget,
  validateDuration,
  validateTargeting,
  validateUrl,
} from 'app/common/react/components/partner/QuickPromote/QuickPromoteForm/ReduxForm/validator';

// Modal management
export type SetQuickReport = {
  type: 'SET_QUICK_REPORT',
  payload: {
    isQuickReport: boolean,
  },
};

export const setQuickReport = (isQuickReport: boolean): SetQuickReport => ({
  type: 'SET_QUICK_REPORT',
  payload: {
    isQuickReport,
  },
});

export type ShowQuickPromotePinSelection = {
  type: 'SHOW_QUICK_PROMOTE_PIN_SELECTION',
  payload: {
    sterlingCreateCampaignUrl: string,
  },
};

// sterlingCreateCampaignUrl is used as a backup URL in case the QP pin select dialog won't work properly.
export const showQuickPromotePinSelection = (
  sterlingCreateCampaignUrl: string,
): ShowQuickPromotePinSelection => ({
  type: 'SHOW_QUICK_PROMOTE_PIN_SELECTION',
  payload: {
    sterlingCreateCampaignUrl,
  },
});

export type SelectQuickPromotePin = {
  type: 'SELECT_QUICK_PROMOTE_PIN',
  payload: {
    pin: any,
  },
};

export function selectQuickPromotePin(pin: any): SelectQuickPromotePin {
  return {
    type: 'SELECT_QUICK_PROMOTE_PIN',
    payload: {
      pin,
    },
  };
}

export type RemoveSelectQuickPromotePinModal = {
  type: 'REMOVE_SELECT_QUICK_PROMOTE_PIN_MODAL',
  payload: {
    removeModal: Function,
  },
};

export function removeSelectQuickPromotePinModal(
  removeModal: Function,
): RemoveSelectQuickPromotePinModal {
  removeModal();
  return {
    type: 'REMOVE_SELECT_QUICK_PROMOTE_PIN_MODAL',
    payload: {
      removeModal,
    },
  };
}

export type ShowQuickPromoteModal = {
  type: 'SHOW_QUICK_PROMOTE_MODAL',
  payload: {
    pin: any,
    isFirstAdCreate: boolean,
    isFromPickaPin: boolean,
  },
};

export function showQuickPromoteModal(
  pin: any,
  isFirstAdCreate: boolean = false,
  isFromPickaPin?: boolean = false,
): ShowQuickPromoteModal {
  return {
    type: 'SHOW_QUICK_PROMOTE_MODAL',
    payload: {
      pin,
      isFirstAdCreate,
      isFromPickaPin,
    },
  };
}

export type RemoveQuickPromoteModal = {
  type: 'REMOVE_QUICK_PROMOTE_MODAL',
  payload: {
    removeModal: Function,
  },
};

export function removeQuickPromoteModal(removeModal: Function): RemoveQuickPromoteModal {
  removeModal();
  return {
    type: 'REMOVE_QUICK_PROMOTE_MODAL',
    payload: {
      removeModal,
    },
  };
}

export type QuickPromoteSetData = {
  type: 'QUICK_PROMOTE_SET_DATA',
  payload: {
    hasValidBillingProfile: boolean,
    hasBusinessAddress: boolean,
    advertiserId: any,
    advertiserCountry: number,
    advertisers: Array<Advertiser>,
  },
};

export function quickPromoteSetData(
  hasValidBillingProfile: boolean,
  hasBusinessAddress: boolean,
  advertiserId: any,
  advertiserCountry: number,
  advertisers: Array<Advertiser>,
): QuickPromoteSetData {
  return {
    type: 'QUICK_PROMOTE_SET_DATA',
    payload: {
      hasValidBillingProfile,
      hasBusinessAddress,
      advertiserId,
      advertiserCountry,
      advertisers,
    },
  };
}
export type QuickPromoteSetInitialFormData = {
  type: 'QUICK_PROMOTE_SET_INITIAL_FORM_DATA',
  payload: {
    budget: number,
    destinationUrl: string,
    duration: string,
    locale: string,
    name: string,
    searchKeywords: Array<KeywordOptionType>,
  },
};

export function quickPromoteSetDataInitialFormData({
  budget,
  destination_url,
  duration,
  locale,
  name,
  search_keywords,
}: {
  budget: number,
  destination_url: string,
  duration: string,
  locale: string,
  name: string,
  search_keywords: Array<KeywordOptionType>,
}): QuickPromoteSetInitialFormData {
  return {
    type: 'QUICK_PROMOTE_SET_INITIAL_FORM_DATA',
    payload: {
      budget,
      destinationUrl: destination_url,
      duration,
      locale,
      name,
      searchKeywords: search_keywords,
    },
  };
}
export type AdvertiserFormModalType = 'QUICK_PROMOTE_BUSINESS_FORM' | 'QUICK_PROMOTE_BILLING_FORM';

export type SubmitInfoFormSucceedData = {
  type: 'QUICK_PROMOTE_SUCCESS_SUBMIT_INFO_FORM',
  payload?: AdvertiserFormModalType,
};

export function submitInfoFormSucceed(
  formType: AdvertiserFormModalType,
): SubmitInfoFormSucceedData {
  return {
    type: 'QUICK_PROMOTE_SUCCESS_SUBMIT_INFO_FORM',
    payload: formType,
  };
}

export type QuickPromoteChangeAdvertiser = {
  type: 'QUICK_PROMOTE_CHANGE_ADVERTISER',
  payload: {
    advertiserId: string,
  },
};

export function quickPromoteChangeAdvertiser(advertiserId: string): QuickPromoteChangeAdvertiser {
  return {
    type: 'QUICK_PROMOTE_CHANGE_ADVERTISER',
    payload: {
      advertiserId,
    },
  };
}

export type openQuickPromoteSelectPinModalFromSterlingData = {
  type: 'QUICK_PROMOTE_PIN_SELECT_PAGE_OPEN_FROM_STERLING',
  payload: {
    sterlingAdvertiser?: AdvertiserShape,
    isFromSterling: boolean,
    isQuickReport: boolean,
  },
};

export function openQuickPromoteSelectPinModalFromSterling(config: {
  sterlingAdvertiser?: AdvertiserShape,
  isQuickReport: boolean,
  isFromSterling: boolean,
}): openQuickPromoteSelectPinModalFromSterlingData {
  const { sterlingAdvertiser, isQuickReport, isFromSterling } = config;
  return {
    type: 'QUICK_PROMOTE_PIN_SELECT_PAGE_OPEN_FROM_STERLING',
    payload: {
      sterlingAdvertiser,
      isQuickReport,
      isFromSterling,
    },
  };
}

export type ShouldOpenQuickPromoteRenderedOnSterlingData = {
  type: 'QUICK_PROMOTE_SHOULD_OPEN_ON_STERLING',
  payload: {
    sterlingAdvertiser?: AdvertiserShape,
    shouldOpenQuickPromoteOnSterling: boolean,
    isQuickReport: boolean,
    isFromSterling: boolean,
  },
};

export function quickPromoteShouldOpenOnSterling(config: {
  sterlingAdvertiser?: AdvertiserShape,
  shouldOpenQuickPromoteOnSterling: boolean,
  isQuickReport: boolean,
  isFromSterling: boolean,
}): ShouldOpenQuickPromoteRenderedOnSterlingData {
  const {
    sterlingAdvertiser,
    shouldOpenQuickPromoteOnSterling,
    isQuickReport,
    isFromSterling,
  } = config;
  return {
    type: 'QUICK_PROMOTE_SHOULD_OPEN_ON_STERLING',
    payload: {
      sterlingAdvertiser,
      shouldOpenQuickPromoteOnSterling,
      isQuickReport,
      isFromSterling,
    },
  };
}

/** all form values updates for QuickPromoteFormV2 */
export function quickPromoteChangeDestinationUrl(destinationUrl: string) {
  return {
    type: 'QUICK_PROMOTE_UPDATE_FORM_URL',
    payload: {
      destinationUrl,
      error: validateUrl(destinationUrl),
    },
  };
}

export function quickPromoteChangeDailyBudget(
  dailyBudget: number,
  currencySymbol: string,
  locale: string,
) {
  return {
    type: 'QUICK_PROMOTE_UPDATE_FORM_BUDGET',
    payload: {
      dailyBudget,
      error: validateBudget(dailyBudget.toString(), currencySymbol, locale),
    },
  };
}

export function quickPromoteChangeDuration(duration: string) {
  return {
    type: 'QUICK_PROMOTE_UPDATE_FORM_DURATION',
    payload: {
      duration,
      error: validateDuration(duration),
    },
  };
}

export function quickPromoteChangeKeywords(searchKeywords: Array<KeywordOptionType>) {
  return {
    type: 'QUICK_PROMOTE_UPDATE_FORM_KEYWORDS',
    payload: {
      searchKeywords,
      error: validateTargeting(searchKeywords),
    },
  };
}

export function quickPromoteSetAges(ages: Array<string>) {
  return {
    type: 'QUICK_PROMOTE_UPDATE_FORM_VALUES',
    payload: {
      ages,
    },
  };
}

export function quickPromoteSetGenders(genders: Array<string>) {
  return {
    type: 'QUICK_PROMOTE_UPDATE_FORM_VALUES',
    payload: {
      genders,
    },
  };
}

export function quickPromoteSetInterests(interests: Array<InterestType>) {
  return {
    type: 'QUICK_PROMOTE_UPDATE_FORM_TARGETING_INTERESTS',
    payload: {
      interests,
      error: validateTargeting(interests),
    },
  };
}

export function quickPromoteChangeIsOngoing(isOngoing: boolean, duration: string) {
  return {
    type: 'QUICK_PROMOTE_UPDATE_IS_ONGOING',
    payload: {
      isOngoing,
      error: isOngoing ? undefined : validateDuration(duration),
    },
  };
}

export function quickPromoteChangeName(name: string, error: ?string) {
  return {
    type: 'QUICK_PROMOTE_UPDATE_FORM_NAME',
    payload: {
      name,
    },
  };
}

export function quickPromoteToggleShortDurationModal(showShortDurationModal: boolean) {
  return {
    type: 'QUICK_PROMOTE_UPDATE_FORM_VALUES',
    payload: {
      showShortDurationModal,
    },
  };
}

export function quickPromoteSetCampaign(campaign: CampaignType) {
  return {
    type: 'QUICK_PROMOTE_UPDATE_FORM_VALUES',
    payload: {
      campaign,
    },
  };
}

export function quickPromoteResetData() {
  return {
    type: 'QUICK_PROMOTE_RESET_DATA',
  };
}

export function quickPromoteResetFormWhenMoveBack() {
  return {
    type: 'QUICK_PROMOTE_RESET_FORM_WHEN_MOVE_BACK',
  };
}

export function showQuickPromoteModalFromPinSelection(
  pin: any,
  isFirstAdCreate: boolean = false,
  isFromPickaPin?: boolean = false,
) {
  return {
    type: 'SHOW_QUICK_PROMOTE_MODAL_FROM_PIN_SELECTION',
    payload: {
      pin,
      isFirstAdCreate,
      isFromPickaPin,
    },
  };
}

export function quickPromoteSetPin(pin: any) {
  return {
    type: 'QUICK_PROMOTE_SET_PIN',
    payload: {
      pin,
    },
  };
}

export function initQuickPromotePage() {
  return {
    type: 'QUICK_PROMOTE_INIT_PAGE',
  };
}

export function setTargetingOptions(targeting: QuickPromoteTargetingType) {
  return { type: 'QUICK_PROMOTE_TARGETING_OPTIONS_SET', payload: { targeting } };
}

// TODO (cyao) unifying with TargetingOptionsReload in
// sterling/redux/actions/quickEdit.js later
export function reloadTargetingOptions() {
  return (dispatch: Function) => {
    import(/* webpackChunkName: "sterlingAPI" */ 'app/sterling/lib/API').then(
      ({ default: API }) => {
        const api = new API();
        const promises = [
          'ads/v3/resources/interests/topics/',
          'ads/v3/resources/targeting_types/',
        ].map((url) => api.get(url));

        Promise.all(promises)
          .then((response) => {
            const topicsResponse = response[0];
            const targetingResponse = response[1];
            const { LOCATION, GENDER, AGE_BUCKET } = targetingResponse;
            const targeting = {
              LOCATION,
              GENDER,
              AGE_BUCKET,
              AUDIENCE: topicsResponse,
            };
            dispatch(setTargetingOptions(targeting));
          })
          .catch(() => {
            // Do nothing
          });
      },
    );
  };
}
